'use client';

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Alert,
  useTheme,
  CircularProgress,
  Snackbar
} from '@mui/material';
import { PaymentOutlined, ContactSupportOutlined, AccessTimeOutlined, FolderOutlined } from '@mui/icons-material';
import QRCode from 'qrcode';
import { apiClient } from '@/services/api';

type PaymentDialogProps = {
  open: boolean;
  onClose: () => void;
  reason?: string;
  message?: string;
};

type PlanItem = {
  id: string; // 使用套餐code作为卡片id
  planId: number;
  name: string;
  price: number;
  duration_value?: number;
  duration_unit?: string;
  projectCredits: number;
  maxUsers: number;
  description?: string;
  popular?: boolean;
};

const formatDurationLabel = (durationValue?: number, durationUnit?: string) => {
  if (!durationValue || !durationUnit) return '';
  switch ((durationUnit || '').toLowerCase()) {
    case 'day': return `${durationValue}天`;
    case 'week': return `${durationValue}周`;
    case 'month': return `${durationValue}个月`;
    case 'quarter': return `${durationValue}个季度`;
    case 'year': return `${durationValue}年`;
    default: return `${durationValue}${durationUnit}`;
  }
};

export default function PaymentDialog({ open, onClose, message }: PaymentDialogProps) {

  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansErr, setPlansErr] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<PlanItem | null>(null);

  const [paymentStep, setPaymentStep] = useState<'select' | 'paying' | 'success' | 'failed'>('select');
  const [subscriptionError, setSubscriptionError] = useState(''); // reserved for future detailed UI
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [orderId, setOrderId] = useState(''); // reserved for display if needed
  const [qrCode, setQrCode] = useState('');
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [statusCheckInterval, setStatusCheckInterval] = useState<number | null>(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const showErrorSnackbar = (msg: string) => { setSnackbarMessage(msg); setSnackbarOpen(true); };
  const handleCloseSnackbar = () => setSnackbarOpen(false);

  // 加载可售卖套餐
  useEffect(() => {
    if (!open) return;
    const fetchPlans = async () => {
      try {
        setPlansLoading(true);
        const res = await apiClient.plans.getList({ status: 'active', t: Date.now() });
        if (!res.success) throw new Error(res.message || '加载套餐失败');
        const rows = (res.data || []).filter((p: any) => !p.is_trial && (Number(p.price_yuan || 0) > 0 || Number(p.price_cents || 0) > 0));
        rows.sort((a: any, b: any) => (a.price_yuan || 0) - (b.price_yuan || 0));
        const mapped: PlanItem[] = rows.map((r: any) => ({
          id: r.code,
          planId: r.id,
          name: r.name,
          price: r.price_yuan || Math.round((r.price_cents || 0) / 100),
          duration_value: r.duration_value,
          duration_unit: r.duration_unit,
          projectCredits: r.project_credits_total,
          maxUsers: Number(r.max_users) || 1,
          description: '',
          popular: false,
        }));
        setPlans(mapped);
        if (mapped.length > 0) setSelectedPlan(mapped[0]);
      } catch (e: any) {
        setPlansErr(e.message);
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, [open]);

  // 生成二维码图片
  useEffect(() => {
    if (qrCode && paymentStep === 'paying') {
      QRCode.toDataURL(qrCode, { width: 200, margin: 2 }).then(setQrCodeImage).catch(() => setQrCodeImage(''));
    }
  }, [qrCode, paymentStep]);

  const handlePlanSelect = (plan: PlanItem) => setSelectedPlan(plan);

  const getPlanIdFromCode = (code: string) => plans.find(p => p.id === code)?.planId || plans[0]?.planId;

  const handlePurchase = async () => {
    if (!selectedPlan || paymentLoading) return;
    setPaymentLoading(true);
    setSubscriptionError('');
    try {
      const resp = await apiClient.payment.createOrder({ plan_id: getPlanIdFromCode(selectedPlan.id) });
      if (resp.success) {
        setQrCode(resp.qr_code || resp.qrCode || '');
        setOrderId(resp.order_id || resp.orderId || '');
        setPaymentStep('paying');
        startStatusCheck(resp.order_id || resp.orderId);
      } else {
        const msg = resp.message || resp.error || '创建支付订单失败';
        setSubscriptionError(msg);
        showErrorSnackbar(msg);
      }
    } catch (e: any) {
      showErrorSnackbar(e.message || '创建支付订单失败');
    } finally {
      setPaymentLoading(false);
    }
  };

  const startStatusCheck = (order: string) => {
    const interval = window.setInterval(async () => {
      try {
        const res = await apiClient.payment.checkStatus(order);
        if (res.success && res.status === 'completed') {
          window.clearInterval(interval);
          setPaymentStep('success');
          setTimeout(() => { onClose(); window.location.reload(); }, 1500);
        } else if (res.status === 'failed') {
          window.clearInterval(interval);
          setPaymentStep('failed');
        }
      } catch (e) { /* 忽略瞬时错误 */ }
    }, 3000);
    setStatusCheckInterval(interval);
    // 超时保护
    window.setTimeout(() => {
      window.clearInterval(interval);
      if (paymentStep === 'paying') setPaymentStep('failed');
    }, 5 * 60 * 1000);
  };

  // 对话框关闭时重置状态
  useEffect(() => {
    if (!open) {
      setPaymentStep('select');
      setQrCode(''); setQrCodeImage(''); setOrderId('');
      setSubscriptionError('');
      if (statusCheckInterval) window.clearInterval(statusCheckInterval);
      setStatusCheckInterval(null);
    }
  }, [open]);

  return (
    <React.Fragment>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PaymentOutlined color="primary" />
            <Typography variant="h5" fontWeight="bold">购买套餐</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {paymentStep === 'select' && (
            <>
              {(message) && (<Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>)}
              {plansErr && (<Alert severity="error" sx={{ mb: 2 }}>{plansErr}</Alert>)}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(plansLoading ? [] : plans).map((plan) => (
                  <Card key={plan.id} onClick={() => handlePlanSelect(plan)} sx={{ cursor: 'pointer', border: selectedPlan?.id === plan.id ? 2 : 1, borderColor: selectedPlan?.id === plan.id ? 'primary.main' : 'divider' }}>
                    {plan.popular && (<Chip label="推荐" color="primary" size="small" sx={{ position: 'absolute', top: 8, left: 16 }} />)}
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box>
                          <Typography variant="h6" fontWeight="bold">{plan.name}</Typography>
                          {!!plan.description && <Typography variant="body2" color="text.secondary">{plan.description}</Typography>}
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="h4" color="primary" fontWeight="bold">¥{Number(plan.price || 0).toLocaleString()}</Typography>
                          <Typography variant="body2" color="text.secondary">{formatDurationLabel(plan.duration_value, plan.duration_unit)}</Typography>
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1.5 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeOutlined color="primary" fontSize="small" />
                        <Typography variant="body1" color="text.secondary">使用期限{formatDurationLabel(plan.duration_value, plan.duration_unit)}（项目数量：{plan.projectCredits > 10000 ? '不限制' : `${plan.projectCredits}个`}）</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <FolderOutlined color="primary" fontSize="small" />
                        <Typography variant="body1" color="text.secondary">子账号数量：{plan.maxUsers > 10000 ? '不限制' : `${plan.maxUsers || 1}个`}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              <Card sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'grey.200', mt: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ContactSupportOutlined color="primary" />
                    <Typography variant="h6" fontWeight="bold">客服联系方式</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <img src="/imgs/kefu.png" alt="客服微信二维码" style={{ width: 180, height: 160, borderRadius: 8, border: '2px solid rgba(0,0,0,0.1)' }} />
                    </Box>
                    <Alert severity="info">目前仅支持微信扫码支付，如需对公转账或开票，请联系客服。</Alert>
                  </Box>
                </CardContent>
              </Card>

              {/* 对公账户信息 */}
              <Card sx={{ mt: 2, bgcolor: 'grey.50' }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                    对公账户信息
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        单位名称：
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        河南昭元绩效评价咨询有限公司
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        账号：
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                        41050167282300002221
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        开户行：
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        中国建设银行股份有限公司郑州众意西路支行
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </>
          )}

          {paymentStep === 'paying' && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h6" gutterBottom>微信扫码支付</Typography>
              {qrCodeImage && (<Box sx={{ my: 2 }}><img src={qrCodeImage} alt="微信支付二维码" style={{ width: 200, height: 200 }} /></Box>)}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">等待支付中...</Typography>
              </Box>
            </Box>
          )}

          {paymentStep === 'success' && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h6" color="success.main" gutterBottom>✅ 支付成功！</Typography>
              <Typography variant="body2" color="text.secondary">页面即将自动刷新...</Typography>
            </Box>
          )}

          {paymentStep === 'failed' && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h6" color="error.main" gutterBottom>❌ 支付失败或超时</Typography>
              <Typography variant="body2" color="text.secondary">请重新选择套餐进行支付</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {paymentStep === 'select' ? (
            <>
              <Button onClick={onClose} size="large">暂不购买</Button>
              <Button variant="contained" size="large" disabled={!selectedPlan || paymentLoading} onClick={handlePurchase} startIcon={paymentLoading ? <CircularProgress size={16} /> : <PaymentOutlined />}>
                {paymentLoading ? '创建订单中...' : selectedPlan ? `购买 ${selectedPlan.name}` : '选择套餐'}
              </Button>
            </>
          ) : paymentStep === 'paying' ? (
            <Button onClick={() => setPaymentStep('select')} size="large">重新选择</Button>
          ) : (
            <>
              <Button onClick={onClose} size="large">关闭</Button>
              {paymentStep === 'failed' && <Button variant="contained" size="large" onClick={() => setPaymentStep('select')}>重新购买</Button>}
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity="warning" sx={{ width: '100%' }}>{snackbarMessage}</Alert>
      </Snackbar>
    </React.Fragment>
  );
}


